// NEW_FILE_JUSTIFIED: UserMenu is a new component; no existing user menu/dropdown exists in layout/
import { useState } from 'react';
import { User, LogOut, Check, Pencil } from 'lucide-react';
import { useAuthActions } from '@convex-dev/auth/react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export function UserMenu() {
  const { signOut } = useAuthActions();
  const currentUser = useQuery(api.users.queries.getCurrentUser);
  const updateName = useMutation(api.users.mutations.updateName);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const displayName = currentUser?.name ?? 'User';
  const initial = displayName.charAt(0).toUpperCase();

  const handleSaveName = async () => {
    if (!editingName?.trim()) return;
    setIsSaving(true);
    try {
      await updateName({ name: editingName.trim() });
      setEditingName(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setEditingName(null);
    }
    setOpen(nextOpen);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className="w-9 h-9 rounded-full bg-primary flex items-center justify-center ml-2 cursor-pointer hover:opacity-90 transition-opacity"
          aria-label="User menu"
        >
          {currentUser?.name ? (
            <span className="text-primary-foreground font-semibold text-sm">{initial}</span>
          ) : (
            <User className="w-4 h-4 text-primary-foreground" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{displayName}</span>
            {currentUser?.email && (
              <span className="text-xs text-muted-foreground">{currentUser.email}</span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {editingName !== null ? (
          <div
            className="px-2 py-1.5"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSaveName();
              } else if (e.key === 'Escape') {
                setEditingName(null);
              }
              e.stopPropagation();
            }}
          >
            <label className="text-xs text-muted-foreground mb-1 block">Display name</label>
            <div className="flex items-center gap-1">
              <Input
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                className="h-8 text-sm"
                maxLength={100}
                autoFocus
                aria-label="Edit display name"
              />
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 shrink-0"
                onClick={handleSaveName}
                disabled={isSaving || !editingName.trim()}
                aria-label="Save name"
              >
                <Check className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <DropdownMenuItem
            onSelect={e => {
              e.preventDefault();
              setEditingName(currentUser?.name ?? '');
            }}
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit Name
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => signOut()}>
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
